import { useState, useEffect, useRef } from 'react';
import Select from '../Select/Select';
import './EditOrganizationModal.css';

// Check if element is inside modal content
function isInsideModalContent(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return target.closest('.modal-content') !== null;
}

interface Organization {
  id: string;
  name: string;
  type: string;
  payment_type: string;
}

interface EditOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: OrganizationFormData) => Promise<void>;
  editingOrg: Organization | null;
}

export interface OrganizationFormData {
  name: string;
  type: string;
  payment_type: string;
}

export default function EditOrganizationModal({
  isOpen,
  onClose,
  onSubmit,
  editingOrg,
}: EditOrganizationModalProps) {
  const [formData, setFormData] = useState<OrganizationFormData>({
    name: '',
    type: 'org',
    payment_type: 'prepaid',
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState('');
  const overlayRef = useRef<HTMLDivElement>(null);
  const mouseDownInsideContent = useRef(false);

  // When editingOrg changes, set form data
  useEffect(() => {
    if (editingOrg) {
      setFormData({
        name: editingOrg.name,
        type: editingOrg.type,
        payment_type: editingOrg.payment_type,
      });
    } else {
      setFormData({
        name: '',
        type: 'org',
        payment_type: 'prepaid',
      });
    }
    // Clear error messages
    setErrors({});
    setSubmitError('');
  }, [editingOrg, isOpen]);

  if (!isOpen) return null;

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Please enter organization name';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setSubmitError('');
    try {
      await onSubmit(formData);
      // Reset form
      setFormData({
        name: '',
        type: 'org',
        payment_type: 'prepaid',
      });
      setSubmitError('');
      onClose();
    } catch (error) {
      console.error('Submit failed:', error);
      setSubmitError(error instanceof Error ? error.message : 'Submit failed, please try again');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof OrganizationFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div
      ref={overlayRef}
      className="modal-overlay"
      onMouseDown={e => {
        mouseDownInsideContent.current = isInsideModalContent(e.target);
      }}
      onClick={e => {
        if (e.target === overlayRef.current && !mouseDownInsideContent.current) {
          onClose();
        }
      }}
    >
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="modal-title">{editingOrg ? 'Edit Organization' : 'Add Organization'}</h2>
          <button className="modal-close" onClick={onClose}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {/* Submit error */}
            {submitError && <div className="blue-modal-error-alert">{submitError}</div>}

            {/* Organization Name */}
            <div className="blue-modal-form-group">
              <label className="blue-modal-form-label">
                Organization Name <span className="blue-modal-required">*</span>
              </label>
              <input
                type="text"
                className={`blue-modal-form-input ${errors.name ? 'error' : ''}`}
                placeholder="Enter organization name"
                value={formData.name}
                onChange={e => handleChange('name', e.target.value)}
              />
              {errors.name && <span className="blue-modal-error-msg">{errors.name}</span>}
            </div>

            {/* Organization Type */}
            <div className="blue-modal-form-group">
              <label className="blue-modal-form-label">
                Organization Type <span className="blue-modal-required">*</span>
              </label>
              <Select
                value={formData.type}
                options={[
                  { value: 'org', label: 'Enterprise' },
                  { value: 'personal', label: 'Personal' },
                ]}
                onChange={value => handleChange('type', String(value))}
              />
            </div>

            {/* Payment Method */}
            <div className="blue-modal-form-group">
              <label className="blue-modal-form-label">
                Payment Method <span className="blue-modal-required">*</span>
              </label>
              <Select
                value={formData.payment_type}
                options={[
                  { value: 'prepaid', label: 'Prepaid' },
                  { value: 'postpaid', label: 'Postpaid' },
                ]}
                onChange={value => handleChange('payment_type', String(value))}
              />
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="blue-modal-btn blue-modal-btn-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="blue-modal-btn blue-modal-btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="blue-modal-loading-spinner"></span>
                  Saving...
                </>
              ) : (
                'Save'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
